import React, { useState, useEffect } from 'react';
import { Database, Download, Upload, Clock, CheckCircle, AlertCircle, RefreshCw, Shield, FileJson, Trash2, Plus, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const BackupRestore = ({ i18n }) => {
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showBackupList, setShowBackupList] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBackupStatus();
    fetchBackupList();
  }, []);

  const fetchBackupStatus = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/admin/backup/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupStatus(response.data);
    } catch (error) {
      console.error('Error fetching backup status:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في جلب حالة النسخ الاحتياطي' : 'Error fetching backup status');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBackupList = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupList(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backup list:', error);
    }
  };

  const handleCreateManualBackup = async () => {
    setIsCreating(true);
    toast.info(i18n.language === 'ar' ? 'جاري إنشاء النسخة الاحتياطية...' : 'Creating backup...');
    
    try {
      const response = await axios.post(`${API}/admin/backup/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(
        i18n.language === 'ar' 
          ? `✅ ${response.data.message} (${response.data.backup.records} سجل)`
          : `✅ Backup created! (${response.data.backup.records} records)`
      );
      fetchBackupStatus();
      fetchBackupList();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في إنشاء النسخة الاحتياطية' : 'Error creating backup');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownloadSpecificBackup = async (filename) => {
    toast.info(i18n.language === 'ar' ? 'جاري التحميل...' : 'Downloading...');
    
    try {
      const response = await axios.get(`${API}/admin/backup/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(i18n.language === 'ar' ? 'تم التحميل بنجاح! ✅' : 'Downloaded successfully! ✅');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في التحميل' : 'Error downloading');
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(
      i18n.language === 'ar' 
        ? `⚠️ هل أنت متأكد من حذف ${filename}؟`
        : `⚠️ Are you sure you want to delete ${filename}?`
    )) return;

    try {
      await axios.delete(`${API}/admin/backup/delete/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(i18n.language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
      fetchBackupList();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error(error.response?.data?.detail || (i18n.language === 'ar' ? 'خطأ في الحذف' : 'Error deleting'));
    }
  };

  const handleDownloadBackup = async () => {
    setIsCreating(true);
    toast.info(i18n.language === 'ar' ? 'جاري إنشاء وتحميل النسخة الاحتياطية...' : 'Creating and downloading backup...');
    
    try {
      const response = await axios.get(`${API}/admin/backup/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or create one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'drivemaster_backup.json';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(i18n.language === 'ar' ? 'تم تحميل النسخة الاحتياطية بنجاح! ✅' : 'Backup downloaded successfully! ✅');
      fetchBackupStatus();
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في تحميل النسخة الاحتياطية' : 'Error downloading backup');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error(i18n.language === 'ar' ? 'يرجى اختيار ملف JSON' : 'Please select a JSON file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedFile) {
      toast.error(i18n.language === 'ar' ? 'يرجى اختيار ملف للاستعادة' : 'Please select a file to restore');
      return;
    }

    if (!window.confirm(
      i18n.language === 'ar' 
        ? '⚠️ هل أنت متأكد من استعادة هذه النسخة الاحتياطية؟ سيتم استبدال بعض البيانات الحالية (المحتوى فقط، ليس المستخدمين).' 
        : '⚠️ Are you sure you want to restore this backup? Some current data will be replaced (content only, not users).'
    )) return;

    setIsRestoring(true);
    toast.info(i18n.language === 'ar' ? 'جاري استعادة البيانات...' : 'Restoring data...');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/admin/backup/restore`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(
        i18n.language === 'ar' 
          ? `تم استعادة البيانات بنجاح! ✅ (${Object.values(response.data.restored_records || {}).reduce((a, b) => a + b, 0)} سجل)`
          : `Data restored successfully! ✅ (${Object.values(response.data.restored_records || {}).reduce((a, b) => a + b, 0)} records)`
      );
      setSelectedFile(null);
      fetchBackupStatus();
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error(
        error.response?.data?.detail || 
        (i18n.language === 'ar' ? 'خطأ في استعادة البيانات' : 'Error restoring data')
      );
    } finally {
      setIsRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return i18n.language === 'ar' ? 'لم يتم بعد' : 'Not yet';
    return new Date(dateString).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'sv-SE');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Database className="w-7 h-7 text-indigo-600" />
              {i18n.language === 'ar' ? 'النسخ الاحتياطي والاستعادة' : 'Säkerhetskopiering & Återställning'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {i18n.language === 'ar' ? 'احفظ بياناتك بأمان واستعدها عند الحاجة' : 'Håll dina data säkra och återställ vid behov'}
            </p>
          </div>
          <Button
            onClick={handleDownloadBackup}
            disabled={isCreating}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
          >
            {isCreating ? (
              <>
                <RefreshCw className="w-4 h-4 me-2 animate-spin" />
                {i18n.language === 'ar' ? 'جاري التحميل...' : 'Laddar ner...'}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 me-2" />
                {i18n.language === 'ar' ? 'تحميل نسخة احتياطية' : 'Ladda ner säkerhetskopia'}
              </>
            )}
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">
                {i18n.language === 'ar' ? 'الجدول التلقائي' : 'Auto Schema'}
              </span>
            </div>
            <p className="text-sm text-blue-600">
              {i18n.language === 'ar' ? 'كل 24 ساعة' : 'Var 24:e timme'}
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-700 mb-2">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">
                {i18n.language === 'ar' ? 'آخر نسخة' : 'Senaste backup'}
              </span>
            </div>
            <p className="text-sm text-green-600">
              {formatDate(backupStatus?.last_auto_backup)}
            </p>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-purple-700 mb-2">
              <Database className="w-5 h-5" />
              <span className="font-semibold">
                {i18n.language === 'ar' ? 'إجمالي السجلات' : 'Totalt antal poster'}
              </span>
            </div>
            <p className="text-sm text-purple-600">
              {backupStatus?.total_records?.toLocaleString() || 0} {i18n.language === 'ar' ? 'سجل' : 'poster'}
            </p>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-emerald-700 mb-2">
              <Shield className="w-5 h-5" />
              <span className="font-semibold">
                {i18n.language === 'ar' ? 'الحالة' : 'Status'}
              </span>
            </div>
            <p className="text-sm text-emerald-600">
              {backupStatus?.auto_backup_enabled 
                ? (i18n.language === 'ar' ? '✅ نشط' : '✅ Aktiv') 
                : (i18n.language === 'ar' ? '❌ معطل' : '❌ Inaktiv')}
            </p>
          </div>
        </div>

        {/* Data Statistics */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <FileJson className="w-5 h-5 text-slate-600" />
            {i18n.language === 'ar' ? 'إحصائيات البيانات' : 'Datastatistik'}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {backupStatus?.statistics && Object.entries(backupStatus.statistics).map(([key, value]) => (
              <div key={key} className="bg-white rounded-lg p-3 text-center border">
                <div className="text-2xl font-bold text-indigo-600">{value}</div>
                <div className="text-xs text-slate-500 capitalize">
                  {key === 'users' ? (i18n.language === 'ar' ? 'مستخدم' : 'Användare') :
                   key === 'subscription_plans' ? (i18n.language === 'ar' ? 'خطط' : 'Planer') :
                   key === 'payment_transactions' ? (i18n.language === 'ar' ? 'معاملات' : 'Transaktioner') :
                   key === 'questions' ? (i18n.language === 'ar' ? 'أسئلة' : 'Frågor') :
                   key === 'lessons' ? (i18n.language === 'ar' ? 'دروس' : 'Lektioner') :
                   key === 'videos' ? (i18n.language === 'ar' ? 'فيديوهات' : 'Videor') :
                   key === 'traffic_signs' ? (i18n.language === 'ar' ? 'إشارات' : 'Skyltar') :
                   key === 'refund_requests' ? (i18n.language === 'ar' ? 'طلبات استرداد' : 'Återbetalningar') :
                   key === 'support_tickets' ? (i18n.language === 'ar' ? 'تذاكر دعم' : 'Supportärenden') :
                   key}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Restore Section */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            {i18n.language === 'ar' ? 'استعادة من نسخة احتياطية' : 'Återställ från säkerhetskopia'}
          </h4>
          <p className="text-sm text-amber-700 mb-3">
            {i18n.language === 'ar' 
              ? '⚠️ تحذير: الاستعادة ستستبدل المحتوى الحالي (الأسئلة، الدروس، الفيديوهات). بيانات المستخدمين لن تتأثر.'
              : '⚠️ Varning: Återställning kommer att ersätta nuvarande innehåll (frågor, lektioner, videor). Användardata påverkas inte.'}
          </p>
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="flex-1 text-sm text-slate-500 file:me-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-100 file:text-amber-700 hover:file:bg-amber-200"
            />
            <Button
              onClick={handleRestoreBackup}
              disabled={!selectedFile || isRestoring}
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-100"
            >
              {isRestoring ? (
                <>
                  <RefreshCw className="w-4 h-4 me-2 animate-spin" />
                  {i18n.language === 'ar' ? 'جاري الاستعادة...' : 'Återställer...'}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 me-2" />
                  {i18n.language === 'ar' ? 'استعادة' : 'Återställ'}
                </>
              )}
            </Button>
          </div>
          {selectedFile && (
            <p className="text-sm text-amber-600 mt-2">
              {i18n.language === 'ar' ? 'الملف المحدد:' : 'Vald fil:'} {selectedFile.name}
            </p>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">
            {i18n.language === 'ar' ? '💡 معلومات مهمة' : '💡 Viktig information'}
          </h4>
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
            <li>
              {i18n.language === 'ar' 
                ? 'يتم إنشاء نسخة احتياطية تلقائية كل 24 ساعة'
                : 'Automatisk säkerhetskopiering sker var 24:e timme'}
            </li>
            <li>
              {i18n.language === 'ar' 
                ? 'يتم إنشاء نسخة احتياطية تلقائياً عند بدء تشغيل الخادم'
                : 'En säkerhetskopia skapas automatiskt när servern startar'}
            </li>
            <li>
              {i18n.language === 'ar' 
                ? 'يتم الاحتفاظ بآخر 10 نسخ احتياطية من كل نوع'
                : 'De senaste 10 säkerhetskopiorna av varje typ sparas'}
            </li>
            <li>
              {i18n.language === 'ar' 
                ? 'احفظ النسخ الاحتياطية في مكان آمن خارج الموقع'
                : 'Spara säkerhetskopior på en säker plats utanför webbplatsen'}
            </li>
          </ul>
        </div>

        {/* Backup List Section */}
        <div className="mt-6 bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900 flex items-center gap-2">
              <List className="w-5 h-5 text-slate-600" />
              {i18n.language === 'ar' ? 'النسخ الاحتياطية المتوفرة' : 'Tillgängliga säkerhetskopior'}
              <span className="text-sm text-slate-500">({backupList.length})</span>
            </h4>
            <div className="flex gap-2">
              <Button
                onClick={handleCreateManualBackup}
                disabled={isCreating}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                {isCreating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 me-1" />
                    {i18n.language === 'ar' ? 'نسخة جديدة' : 'Ny backup'}
                  </>
                )}
              </Button>
              <Button
                onClick={() => { fetchBackupList(); fetchBackupStatus(); }}
                size="sm"
                variant="outline"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {backupList.length === 0 ? (
            <p className="text-center text-slate-500 py-4">
              {i18n.language === 'ar' ? 'لا توجد نسخ احتياطية' : 'Inga säkerhetskopior'}
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {backupList.map((backup, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    backup.type === 'startup' ? 'bg-blue-50 border-blue-200' :
                    backup.type === 'manual' ? 'bg-green-50 border-green-200' :
                    backup.type === 'auto' ? 'bg-purple-50 border-purple-200' :
                    'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        backup.type === 'startup' ? 'bg-blue-200 text-blue-800' :
                        backup.type === 'manual' ? 'bg-green-200 text-green-800' :
                        backup.type === 'auto' ? 'bg-purple-200 text-purple-800' :
                        'bg-slate-200 text-slate-800'
                      }`}>
                        {backup.type === 'startup' ? (i18n.language === 'ar' ? 'بدء التشغيل' : 'Start') :
                         backup.type === 'manual' ? (i18n.language === 'ar' ? 'يدوي' : 'Manuell') :
                         backup.type === 'auto' ? (i18n.language === 'ar' ? 'تلقائي' : 'Auto') :
                         backup.type}
                      </span>
                      <span className="text-sm font-medium text-slate-700">{backup.filename}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {backup.size_mb} MB • {backup.records} {i18n.language === 'ar' ? 'سجل' : 'poster'}
                      {backup.created_at && ` • ${new Date(backup.created_at).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'sv-SE')}`}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleDownloadSpecificBackup(backup.filename)}
                      size="sm"
                      variant="outline"
                      className="h-8"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteBackup(backup.filename)}
                      size="sm"
                      variant="outline"
                      className="h-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupRestore;
